/**
 * PRO Work Units (WU) — the single frontend source of truth for the
 * work-unit denomination (2026-08 pricing migration).
 *
 * Prest0 prices in finished work, not tokens. Every user-facing capacity,
 * usage, and balance surface displays PRO Work Units; raw provider token
 * counts are internal-only and must never render in UI (2026-08-13 Miles
 * directive — supersedes the tokens-in/tokens-out displays).
 *
 * The Standard Rate is the published conversion for on-demand work, work
 * beyond a plan's allocation, and top-ups: exactly $2.18 per Work Unit.
 * Committed plans buy WU cheaper (see WU_PRICING ladder), but every conversion
 * between a charged dollar amount and displayed WU happens at the Standard
 * Rate. Billing is metered: each task is charged for the work it performed
 * (functions/src/lib/accountExpense.ts), and the rate card below is typical
 * usage, for planning (Miles, 2026-10-11).
 *
 * Do NOT duplicate these constants. Wallet, chat, and Activity surfaces all
 * import from here so the displayed numbers reconcile with each other.
 */

import type { LadderTier } from './planTiers';

/** Published Standard Rate: on-demand / beyond the allocation / top-ups. Exactly $2.18. */
export const STANDARD_RATE_USD_PER_WU = 2.18;

/**
 * Compact unit label rendered next to amounts (e.g. "142.5 pr0").
 * 2026-08-13 owner directive: the abbreviation is lowercase pr0 (p-r-zero,
 * matching the brand lockup) — never "WU", and never uppercased except
 * where a sentence-start demands it. In rich UI the zero renders as the
 * prest0 logo via the Pr0Unit component; this string is the plain-text
 * form for aria-labels, CSV, and copied values. PUBLIC surfaces spell out
 * "PRO Work Units" on first mention (WU_FULL_NAME) and use pr0 only after
 * introducing it.
 */
export const WU_LABEL = 'pr0';

/** Lowercase text form matching the brand lockup ("pr0"). */
export const WU_LABEL_LOWER = 'pr0';

/** Full product name for headings and first mentions. */
export const WU_FULL_NAME = 'PRO Work Units';

/** Singular form for copy. */
export const WU_SINGULAR = 'PRO Work Unit';

/**
 * pr0 a month in one Prest0n employee (docs/PLAN-per-employee-pricing-20261030.md, ruling R1): the finished work of
 * an average employee's productive month, 73.7 hours. The unit every price on the published ladder is per. The
 * founding cohort's allocation grows on the schedule in config/employee-unit.json (src/config/employeeUnit.generated.ts);
 * this is the launch row, which a gate holds equal to it. The name is kept from the 2,200-pr0 seat it replaced because
 * two gates and the guest digest read it as text.
 */
export const MONTHLY_WU_PER_PRESTON = 450;

/**
 * The published commitment ladder, per Prest0n employee per month (edition 2026-11, src/config/planTiers.ts; the
 * cents Stripe charges are TIER_CONFIG's in functions/src/billing/planCatalog.ts, held equal by
 * tests/billing/ladderMath.test.ts). Each price is its $ per pr0 x 450, rounded half up.
 * Invariant (pricing master chart rule one): price per pr0 only falls as commitment rises — keep this monotonic
 * through every future change. Three employees on the annual commitment carry the old one-Prest0n discount (ruling R2).
 *
 * LAYOUT (text readers depend on it): a plain literal with no imports or expressions — the guest digest evaluates it
 * (scripts/generate-guest-site-digest.mjs) and the pricing-sync gate reads every `usdPerWu:` out of it
 * (scripts/check-public-pricing-sync.mjs). One rung per line; the object's own closer is the first `\n}` in it.
 */
export const WU_PRICING = {
  /** List price — on-demand · beyond the allocation · top-ups: the standard rate for one employee's month. */
  standard: { monthlyUsd: 981, usdPerWu: 2.18, label: 'standard rate' },
  /** Monthly hire — any number of employees, month to month, cancel anytime. */
  monthly: { monthlyUsd: 765, usdPerWu: 1.7, savePct: 22, label: 'monthly hire', tier: 'emp_monthly' },
  /**
   * The annual rungs: a 12-month commitment billed monthly, priced by team size. Contiguous (each rung's minSeats is
   * the last one's maxSeats + 1) and strictly falling; past the last rung, larger organizations book a meeting.
   */
  annualRungs: [
    { key: 'annual1', minSeats: 1, maxSeats: 2, monthlyUsd: 698, usdPerWu: 1.55, savePct: 29, label: 'annual · 1–2 employees', tier: 'emp_annual_1', prepayTier: 'emp_prepay_1' },
    { key: 'annual3', minSeats: 3, maxSeats: 8, monthlyUsd: 653, usdPerWu: 1.45, savePct: 33, label: 'annual · 3–8 employees', tier: 'emp_annual_3', prepayTier: 'emp_prepay_3' },
    { key: 'annual9', minSeats: 9, maxSeats: 14, monthlyUsd: 603, usdPerWu: 1.34, savePct: 39, label: 'annual · 9–14 employees', tier: 'emp_annual_9', prepayTier: 'emp_prepay_9' },
    { key: 'annual15', minSeats: 15, maxSeats: 29, monthlyUsd: 563, usdPerWu: 1.25, savePct: 43, label: 'annual · 15–29 employees', tier: 'emp_annual_15', prepayTier: 'emp_prepay_15' },
    { key: 'annual30', minSeats: 30, maxSeats: 59, monthlyUsd: 527, usdPerWu: 1.17, savePct: 46, label: 'annual · 30–59 employees', tier: 'emp_annual_30', prepayTier: 'emp_prepay_30' },
    { key: 'annual60', minSeats: 60, maxSeats: 149, monthlyUsd: 495, usdPerWu: 1.1, savePct: 50, label: 'annual · 60–149 employees', tier: 'emp_annual_60', prepayTier: 'emp_prepay_60' },
    { key: 'annual150', minSeats: 150, maxSeats: 299, monthlyUsd: 468, usdPerWu: 1.04, savePct: 52, label: 'annual · 150–299 employees', tier: 'emp_annual_150', prepayTier: 'emp_prepay_150' },
  ],
  // No overtime multiplier and no sprint blocks (removed 2026-10-11): neither was
  // ever built. Work beyond the allocation draws the wallet at the Standard Rate.
  /**
   * Prepay the 12 months up front: an ADDITIONAL 20% off the team size's annual rate (2026-08-16 owner directive —
   * it is a discount, never bonus work units; the allocation is the same as the annual rung's). Kept in cents:
   * rung cents x 80 / 100 a month, x 12 for the year's one payment ($558.40 and $6,700.80 for one employee).
   */
  prepayDiscountPct: 20,
  /** The most employees one organization hires here; from the next, larger organizations book a meeting. */
  selfServeMaxSeats: 299,
} as const;

// ── The one hire calculator (2026-08-16 owner directive) ────────────────────
// The buyer toggles cadence and team size, and the rate + % discount recompute live. Both /pricing and
// /subscription price through this single function so they can never disagree — and neither can the backend
// TIER_CONFIG, whose cents these mirror (functions/src/billing/planCatalog.ts).

export type HireCadence = 'monthly' | 'annual' | 'prepay';

export type AnnualRung = (typeof WU_PRICING.annualRungs)[number];

/** A team size the ladder sells: a whole number from 1 to the self-serve ceiling (degenerate inputs clamp). */
export function clampSeats(seats: number): number {
  const n = Math.floor(Number(seats)) || 1;
  return Math.min(WU_PRICING.selfServeMaxSeats, Math.max(1, n));
}

/** The annual rung whose team sizes hold this count. */
export function annualSeatRung(seats: number): AnnualRung {
  const n = clampSeats(seats);
  return WU_PRICING.annualRungs.find((r) => n >= r.minSeats && n <= r.maxSeats) ?? WU_PRICING.annualRungs[WU_PRICING.annualRungs.length - 1];
}

export interface HireQuote {
  /** Backend tier id (src/config/planTiers.ts; functions/src/billing/planCatalog.ts TIER_CONFIG). */
  tier: LadderTier;
  cadence: HireCadence;
  /** The team size quoted (1 to the self-serve ceiling). */
  seats: number;
  /** Cents per employee per billing interval: exactly what Stripe charges for one seat. */
  perSeatCents: number;
  /** The billing interval those cents are for: a prepaid year bills once a year. */
  interval: 'month' | 'year';
  /** Effective monthly-equivalent price per employee, in dollars (prepay: after the 20%, cents kept: 558.4). */
  perSeatMonthlyUsd: number;
  /** Effective committed $ per pr0. */
  usdPerWu: number;
  /** % saved vs the Standard Rate — the headline number. */
  savePct: number;
  /** pr0 per employee per month at launch — prepay is a discount, not capacity. */
  wuPerSeat: number;
  /** Monthly-equivalent total for the team, in dollars. */
  totalMonthlyUsd: number;
  totalWuMonthly: number;
  /** Prepay only: the single payment for the year, for the team, in dollars. */
  upfrontTotalUsd: number | null;
}

export function hireQuote(cadence: HireCadence, seats: number): HireQuote {
  const n = clampSeats(seats);
  const standardCents = WU_PRICING.standard.monthlyUsd * 100;
  if (cadence === 'monthly') {
    const cents = WU_PRICING.monthly.monthlyUsd * 100;
    return {
      tier: WU_PRICING.monthly.tier,
      cadence,
      seats: n,
      perSeatCents: cents,
      interval: 'month',
      perSeatMonthlyUsd: cents / 100,
      usdPerWu: WU_PRICING.monthly.usdPerWu,
      savePct: WU_PRICING.monthly.savePct,
      wuPerSeat: MONTHLY_WU_PER_PRESTON,
      totalMonthlyUsd: (cents * n) / 100,
      totalWuMonthly: MONTHLY_WU_PER_PRESTON * n,
      upfrontTotalUsd: null,
    };
  }
  const rung = annualSeatRung(n);
  const rungCents = rung.monthlyUsd * 100;
  if (cadence === 'prepay') {
    // whole cents: a rung is whole dollars, so 80% of it is a whole number of cents (multiples of 80¢)
    const monthCents = (rungCents * (100 - WU_PRICING.prepayDiscountPct)) / 100;
    const yearCents = monthCents * 12;
    return {
      tier: rung.prepayTier,
      cadence,
      seats: n,
      perSeatCents: yearCents,
      interval: 'year',
      perSeatMonthlyUsd: monthCents / 100,
      usdPerWu: Math.round(monthCents / MONTHLY_WU_PER_PRESTON) / 100,
      // prepay is not a published rung, so its saving is computed from its price
      savePct: Math.round((1 - monthCents / standardCents) * 100),
      wuPerSeat: MONTHLY_WU_PER_PRESTON,
      totalMonthlyUsd: (monthCents * n) / 100,
      totalWuMonthly: MONTHLY_WU_PER_PRESTON * n,
      upfrontTotalUsd: (yearCents * n) / 100,
    };
  }
  // C1 (GSD revamp, 2026-10-15): a base rung says the saving the ladder PUBLISHES, so the hire card and the published
  // ladder agree.
  return {
    tier: rung.tier,
    cadence,
    seats: n,
    perSeatCents: rungCents,
    interval: 'month',
    perSeatMonthlyUsd: rung.monthlyUsd,
    usdPerWu: rung.usdPerWu,
    savePct: rung.savePct,
    wuPerSeat: MONTHLY_WU_PER_PRESTON,
    totalMonthlyUsd: (rungCents * n) / 100,
    totalWuMonthly: MONTHLY_WU_PER_PRESTON * n,
    upfrontTotalUsd: null,
  };
}

/**
 * A price for display: whole dollars when there are no cents ("$1,959"), otherwise two decimals ("$558.40",
 * "$6,700.80"). The one money format for every published price, so a prepaid figure is never shown rounded to a
 * dollar it is not charged.
 */
export function formatPriceUsd(usd: number): string {
  const cents = Math.round((Number(usd) || 0) * 100);
  const whole = cents % 100 === 0;
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 })}`;
}

/**
 * The published Work Rate Card — TYPICAL pr0 per finished task type, for
 * planning. It is guidance, never the charge: billing is metered, so each
 * task is charged for the work it performed and shown task by task on the
 * activity page; a heavier task uses more (Miles, 2026-10-11 — the card used
 * to call itself flat, with overruns "always free", which the meter never
 * honored). The rows that were not typical usage are gone: the 0-pr0
 * "overruns · retries · failovers · thinking depth" row, and the sub-hourly
 * monitoring Routine at "1 pr0 per day, unlimited runs" (each run is a
 * Routine run; see that row's note below for the arithmetic).
 *
 * ── PROVENANCE (2026-08-19 rate-card recalibration) ────────────────────────
 * The original rates were set in the 2026-08-13 pricing migration BEFORE any
 * consumption data existed. They were far too high. Measured against the
 * production wallet ledger (`scripts/pr0-consumption-analysis.mjs`, run by
 * `.github/workflows/pr0-consumption-telemetry.yml` — complete scan, 5,749
 * expense rows, 2026-01-28 .. 2026-08-19):
 *
 *   task type            published   measured p50      measured p90     n
 *   deep-research memo      40        5.30 (2.29/30d)     7.36        156 sessions (all-time)
 *   drafted document         8        0.29                2.25         20 (all-time)
 *   translation              6        0.25 (0.70/30d)     2.52 (30d)   33 (30d)
 *   document intake/file     2        0.31                2.05          7 (all-time)
 *   task turn                1        0.13                0.97        227 (30d; all-time n
 *                                                                      not broken out — the
 *                                                                      percentiles here ARE
 *                                                                      the all-time ones)
 *
 * So the card overstated a deep-research memo by 5.4x at p90 and 7.5x at p50 —
 * a customer reading /pricing believed 2,200 pr0 bought 55 memos when it
 * actually buys ~275 at the new rate and ~400+ at measured p50.
 *
 * NEW RATES ARE SET AT ≈p90, NOT p50, deliberately: published capacity is a
 * promise the platform has to keep on a bad month, not a typical-case boast.
 * p90 is also the stable statistic here — it came out at 7.36 for deep
 * research in BOTH the 30-day window (n=14) and the all-time ledger (n=156),
 * while p50 moved from 5.30 to 2.29 as the model ladder got cheaper. Setting
 * rates at p50 would publish ~400 memos/month and put half of all runs over
 * the line the moment prices move.
 *
 * A task in the tail beyond p90 is metered at what it used. Observed worst case
 * for a single deep-research deliverable in the full ledger was 25.76 pr0.
 *
 * ONE STATED EXCEPTION TO THE p90 RULE — the task turn. Every other row clears
 * its 30-day p90 (memo 8 ≥ 7.36, document 3 ≥ 2.84, translation 3 ≥ 2.52,
 * intake 2 ≈ 2.05). The task turn stays at 1, which is its ALL-TIME p90 (0.97)
 * but is BELOW its 30-day p90 of 2.05. Deliberate: "one task turn = one work
 * unit" is the anchor that makes the unit legible, and moving it to 2 would
 * halve published task capacity while making the card harder to explain than
 * the thing it prices. A heavy turn is metered at what it used.
 *
 * RE-MEASURE BEFORE CHANGING ANY RATE BELOW: re-run the telemetry workflow.
 * Do not hand-edit these numbers from intuition — that is how they got wrong.
 */
export const WORK_RATE_CARD = [
  {
    task: 'deep-research memo',
    wu: 8,
    unit: 'per memo',
    receive: 'cited long-form memo with sources — PDF + Word, in minutes',
  },
  {
    task: 'drafted document',
    wu: 3,
    unit: 'per document',
    receive: 'contract, agreement, demand letter, memo — attorney-review flagged where legal',
  },
  {
    task: 'certified-style translation',
    wu: 3,
    unit: 'per document',
    receive: 'per document, EN↔ES, PDF + Word always',
  },
  {
    task: 'document intake & analysis',
    wu: 2,
    unit: 'per file',
    receive: 'OCR, reading, indexing — batches returned as one deliverable',
  },
  {
    // 2026-08-19: task turns measure p50 0.13 / p90 0.97 — 1 pr0 is the one
    // rate on this card that was already right, so it is unchanged.
    // ⚠️ VOICE IS THE EXCEPTION AND IS KNOWINGLY UNDER-PRICED HERE. The single
    // voice row in the ledger metered 8.50 pr0, and tier-10-voice has NO entry
    // in MODEL_PRICING at all (src/config/aiModelPricing.ts) — every voice
    // generation falls through to the 'unknown' $1/$5-per-million floor, so
    // even that 8.50 is a placeholder, not a real provider rate. n=1 is far
    // too thin to publish a number from. Price tier-10 and re-measure BEFORE
    // splitting voice onto its own row; until then a voice minute is metered
    // at what it used. See the report for the exposure math.
    //
    // A MONITORING ROUTINE IS RUNS OF THIS ROW (2026-10-11). The card used to
    // publish a sub-hourly monitoring Routine at "1 pr0 per day, unlimited runs"
    // — the worst-priced row it had: every Routine run is a full metered turn
    // (p50 0.13, p90 0.97 pr0), so a 5-minute Routine is 288 runs a day, about
    // 37-280 pr0. Every sub-30-minute cadence activates only after the person
    // confirms a presented $/hour and $/day estimate
    // (ROUTINE_CONFIRMATION_THRESHOLD_SECONDS, MIN_INTERVAL_SECONDS and
    // MAX_ROUTINES_PER_ACCOUNT in functions/src/prest0nSession/routines.ts), so
    // the truthful number reaches the person there; the card no longer
    // contradicts it.
    task: 'task turn · Routine run · voice minute',
    wu: 1,
    unit: 'each',
    receive: 'a typical turn end to end — a longer one uses more, and every Routine run counts',
  },
] as const;

/**
 * The published worked example — what one Prest0n employee's typical 450-pr0
 * month buys (mirrors the Pricing Public sheet). What a month actually used is
 * shown task by task on the activity page.
 */
/*
 * The worked example — recut 2026-10-30 for one Prest0n employee (450 pr0, the
 * same card rows: 5 memos, 25 documents, 30 translations, 70 files, 105 task
 * turns, about 73 hours of finished work at the ROI dataset's hours per item;
 * 75 until every returned hour was valued at its full cost, 2026-11-02).
 * It was 25 / 120 / 150 / 350 / 490 for the 2,200-pr0 seat it replaced (recut
 * 2026-08-19 against the recalibrated rates).
 *
 * NUMERIC, not display strings (2026-08-19 owner directive: "easy to use
 * calculator with preloaded numbers"). This same array IS the calculator's
 * preset on /pricing and /subscription — `count` is the starting position of
 * each slider and `wuEach` its rate — so the worked example and the thing the
 * buyer drags can never disagree. Rendering adds the "~".
 *
 * INVARIANTS, enforced by scripts/check-public-pricing-sync.mjs:
 *   - every wuEach matches its WORK_RATE_CARD row
 *   - sum(count x wuEach) === MONTHLY_WU_PER_PRESTON exactly
 */
export const TYPICAL_MONTH = [
  { key: 'memo', count: 5, wuEach: 8, item: 'deep-research memos' },
  { key: 'document', count: 25, wuEach: 3, item: 'drafted documents' },
  { key: 'translation', count: 30, wuEach: 3, item: 'certified-style translations' },
  { key: 'file', count: 70, wuEach: 2, item: 'files processed' },
  { key: 'task', count: 105, wuEach: 1, item: 'tasks, Routines & voice minutes' },
] as const;

/** pr0 consumed by one worked-example line. */
export const typicalMonthLineWu = (row: { count: number; wuEach: number }): number =>
  row.count * row.wuEach;

/**
 * The most of ONE product a month can hold — one employee's 450 pr0 ÷ its
 * rate. This is what the old SINGLE_PRODUCT_CEILINGS list published as static
 * prose; it is now the maximum of each calculator slider, so a buyer discovers
 * it by dragging instead of reading a second list of numbers.
 */
export const singleProductCeiling = (wuEach: number): number =>
  Math.floor(MONTHLY_WU_PER_PRESTON / wuEach);

/*
 * (SINGLE_PRODUCT_CEILINGS was removed 2026-08-19. It published the same four
 * numbers as static prose beside the worked month and the rate table — three
 * lists of the same arithmetic on one page. The ceilings are now the maximum
 * of each calculator slider, via singleProductCeiling() above.)
 */

/** Convert a user-charged dollar amount to displayed WU at the Standard Rate. */
export const usdToWu = (usd: number): number => usd / STANDARD_RATE_USD_PER_WU;

/** Convert WU back to dollars at the Standard Rate. */
export const wuToUsd = (wu: number): number => wu * STANDARD_RATE_USD_PER_WU;

/**
 * Format a WU quantity for display. WU live at human scale (a Prest0n
 * employee's month is 450) so precision tapers with magnitude; tiny nonzero
 * amounts floor at "<0.01" rather than rendering long decimal tails.
 */
export function formatWu(value: number, showPrefix = false): string {
  if (!Number.isFinite(value)) return '0';
  const prefix = showPrefix && value > 0 ? '+' : value < 0 ? '-' : '';
  const abs = Math.abs(value);

  if (abs === 0) return '0';
  if (abs < 0.01) return `${prefix}<0.01`;
  if (abs < 100) return prefix + abs.toFixed(2).replace(/\.?0+$/, '');
  if (abs < 1000) return prefix + abs.toFixed(1).replace(/\.?0+$/, '');
  return (
    prefix +
    abs.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
  );
}

/**
 * DETAIL-surface formatter (2026-08-14 Miles directive: "more decimal places
 * in a strategic way"). Per-call rows — the ⓘ usage popup's per-generation
 * lines, activity drill-downs, expense line items — must show the real
 * quantity, never the "<0.01" floor: a single model call is often
 * 0.0005–0.005 pr0 and the floor made every cheap call look identical.
 *
 * Contract by magnitude:
 *   ≥ 100    → same taper as formatWu (headline territory)
 *   ≥ 0.01   → up to 3 decimals, trailing zeros trimmed
 *   < 0.01   → at least TWO significant figures (0.00042 renders as itself),
 *              capped at 8 decimals; true zero renders '0'
 *
 * HEADLINE surfaces (wallet balance, daily/monthly totals, plan meters) keep
 * formatWu's taper — clean numbers where precision is noise.
 */
export function formatWuDetail(value: number, showPrefix = false): string {
  if (!Number.isFinite(value)) return '0';
  const prefix = showPrefix && value > 0 ? '+' : value < 0 ? '-' : '';
  const abs = Math.abs(value);

  if (abs === 0) return '0';
  if (abs >= 100) return formatWu(value, showPrefix);
  if (abs >= 0.01) return prefix + abs.toFixed(3).replace(/\.?0+$/, '');
  const decimals = Math.min(8, -Math.floor(Math.log10(abs)) + 1);
  return prefix + abs.toFixed(decimals).replace(/0+$/, '').replace(/\.$/, '');
}

/** Detail formatter with the unit attached: "0.0004 pr0". */
export function formatWuDetailWithUnit(value: number, showPrefix = false): string {
  return `${formatWuDetail(value, showPrefix)} ${WU_LABEL}`;
}

/** Format with the unit attached: "142.5 WU". */
export function formatWuWithUnit(value: number, showPrefix = false): string {
  return `${formatWu(value, showPrefix)} ${WU_LABEL}`;
}

/** Format a charged dollar amount directly as WU: formatWuFromUsd(4.36) → "2 WU". */
export function formatWuFromUsd(usd: number, showPrefix = false): string {
  return formatWu(usdToWu(usd), showPrefix);
}
